import { Body1, ButtonText, Headline3, Headline5, Subtitle1 } from 'stories/elements/Typography/Text'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import theme from 'stories/utils/theme'
import useMedia from 'lib/hooks/useMedia'
import React from 'react'
import styled, { css } from 'styled-components'
import DynamicButton from './DynamicButton'

interface ContainerProps {
    backgroundColor: string
}
const Container = styled.div<ContainerProps>`
    height: 100%;
    position: relative;
    background-color: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : theme.colors.backgroundColorGray)};
`

const Section = styled.div`
    height: 100%;
    display: grid;
    position: relative;
    z-index: 1;
    padding: 10%;
    grid-template-columns: 1fr 1fr;
    place-content: start;
    gap: 30px;
    @media (max-width: 1050px) {
        grid-template-columns: 1fr;
    }
    .text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 5px;
        min-height: 450px;
        .subtext {
            width: 60%;
        }
        @media (max-width: 1050px) {
            padding-top: 150px;
            flex-direction: column;
            align-items: center;
            .subtext {
                width: 100%;
                text-align: center;
            }
        }
        @media (max-width: 750px) {
            padding-top: 100px;
            flex-direction: column;
            align-items: center;
            min-height: 300px;
        }
    }
`

interface BackgroundColorProps {
    placement: 'top' | 'bottom'
    color?: string
}

const BackgroundColorSection = styled.div<BackgroundColorProps>`
    width: 100%;
    height: 225px;
    z-index: 0;
    background-color: ${({ color }) => color ?? theme.colors.backgroundColorGray};
    position: absolute;
    left: 0;
    ${({ placement }) =>
        placement === 'top'
            ? css`
                  top: 0;
              `
            : css`
                  bottom: 0;
              `};
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 30px;

    @media (max-width: 1050px) {
        display: flex;
        flex-direction: column;
        align-items: center;
        grid-template-columns: 1fr;
    }
    @media (max-width: 750px) {
        grid-template-columns: 1fr;
    }
`

const BenefitCard = styled.div`
    min-height: 120px;
    width: 100%;
    padding: 15px;
    background: ${theme.colors.white};
    border: 3px solid ${theme.colors.accentBlue};
    box-shadow: 38px 43px 45px rgba(0, 0, 0, 0.04), 4px 4px 0px ${theme.colors.accentBlue};
    border-radius: 15px;
    display: grid;
    row-gap: 5px;
    @media (max-width: 750px) {
        height: auto;
        width: 100%;
    }
`

interface Props {
    items: {
        title: string
        subtitle: string
    }[]
    buttonText: string
    buttonLink: string
    title: string
    text: string
    subtitle: string
    colorBottom: string
    colorTop: string
    backgroundColor: string
    buttonColor: string
    subtitleColor: string
    textColor: string
    titleColor: string
    onClick?: () => void
}

const BenefitColumn = ({
    items,
    buttonText,
    buttonLink,
    title,
    subtitle,
    text,
    colorBottom,
    colorTop,
    backgroundColor,
    buttonColor,
    subtitleColor,
    textColor,
    titleColor,
    onClick,
}: Props) => {
    const lg = useMedia(deviceSize.lg)
    return (
        <Container backgroundColor={backgroundColor}>
            <BackgroundColorSection placement="top" color={colorTop} />

            <Section>
                <div className="text">
                    <Subtitle1 color={titleColor ?? theme.colors.knowunityBlue}>{subtitle}</Subtitle1>
                    <Headline3 color={subtitleColor ?? theme.colors.white} marginBottom={30} className="subtext">
                        {title}
                    </Headline3>
                    {text ? (
                        <Body1
                            color={textColor ?? theme.colors.headlineDark}
                            marginBottom={lg ? 25 : 50}
                            className="subtext"
                        >
                            {text}
                        </Body1>
                    ) : null}
                    {buttonLink ? (
                        <DynamicButton
                            href={buttonLink}
                            width={230}
                            height={60}
                            borderRadius={theme.borderRadius.normal}
                            color={buttonColor ?? theme.colors.knowunityBlue}
                            onClick={onClick}
                        >
                            <ButtonText color={theme.colors.white}>{buttonText}</ButtonText>
                        </DynamicButton>
                    ) : null}
                </div>
                <Grid>
                    {items.map((item, i) => (
                        <BenefitCard key={`information-card-${i}`}>
                            <Headline5 color={theme.colors.accentBlue} marginBottom={0}>
                                {item.title}
                            </Headline5>
                            <Body1 color={theme.colors.body1Black}>{item.subtitle}</Body1>
                        </BenefitCard>
                    ))}
                </Grid>
            </Section>
            <BackgroundColorSection placement="bottom" color={colorBottom} />
        </Container>
    )
}

export default BenefitColumn
